import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Music, ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { useStudentAuth, formatApiErrorDetail } from "../context/StudentAuth";
import { Logo } from "../components/site/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "signin", label: "Sign in" },
  { id: "register", label: "Register" },
];

const InputRow = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30]">{label}</span>
    <input
      {...props}
      className="mt-2 w-full bg-transparent border border-[rgba(28,22,16,0.2)] px-4 py-3 text-sm text-[#1c1610] focus:outline-none focus:border-[#DB0909] placeholder:text-[#4a3e30]/50 transition-colors"
    />
  </label>
);

const PasswordRow = ({ label, value, onChange, placeholder = "••••••••", testId }) => {
  const [show, setShow] = useState(false);
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30]">{label}</span>
      <div className="mt-2 flex items-stretch border border-[rgba(28,22,16,0.2)] focus-within:border-[#DB0909] transition-colors">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          data-testid={testId}
          className="flex-1 bg-transparent px-4 py-3 text-sm text-[#1c1610] focus:outline-none placeholder:text-[#4a3e30]/50"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="px-3 text-[#4a3e30] hover:text-[#DB0909] transition-colors"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </label>
  );
};

export default function Login() {
  const nav = useNavigate();
  const location = useLocation();
  const { user, login, register, checked } = useStudentAuth();
  const initialTab = new URLSearchParams(location.search).get("tab") || "signin";
  const initialEmail = new URLSearchParams(location.search).get("email") || "";
  const [tab, setTab] = useState(TABS.some((t) => t.id === initialTab) ? initialTab : "signin");

  const [identifier, setIdentifier] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [regEmail, setRegEmail] = useState(initialEmail);
  const [regUsername, setRegUsername] = useState("");
  const [regName, setRegName] = useState("");
  const [regPassword, setRegPassword] = useState("");
  // OTP for register
  const [regOtpSent, setRegOtpSent] = useState(false);
  const [regOtpCode, setRegOtpCode] = useState("");
  const [regOtpVerified, setRegOtpVerified] = useState(false);
  const [regOtpSending, setRegOtpSending] = useState(false);
  const [regOtpCooldown, setRegOtpCooldown] = useState(0);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStage, setForgotStage] = useState("email");   // email → verify
  const [forgotCode, setForgotCode] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    if (checked && user) {
      const from = new URLSearchParams(location.search).get("from") || "/";
      nav(from, { replace: true });
    }
  }, [user, checked, nav, location.search]);

  const clearMsgs = () => { setError(""); setInfo(""); };

  const switchTab = (id) => { clearMsgs(); setTab(id); setForgotStage("email"); };

  const handleSignin = async (e) => {
    e.preventDefault();
    clearMsgs();
    setSubmitting(true);
    try {
      await login(identifier.trim(), password);
      const from = new URLSearchParams(location.search).get("from") || "/";
      nav(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (regOtpCooldown <= 0) return;
    const t = setInterval(() => setRegOtpCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [regOtpCooldown]);

  const sendRegisterOtp = async () => {
    clearMsgs();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail.trim())) { setError("Enter a valid email first."); return; }
    setRegOtpSending(true);
    try {
      await axios.post(`${API}/api/otp/email/send`, { email: regEmail.trim().toLowerCase() });
      setRegOtpSent(true);
      setRegOtpCooldown(45);
      setInfo("Verification code sent — check your inbox (and spam).");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not send code. Please retry.");
    } finally {
      setRegOtpSending(false);
    }
  };

  const verifyRegisterOtp = async () => {
    clearMsgs();
    if (regOtpCode.length !== 6) { setError("Enter the 6-digit code from your email."); return; }
    try {
      await axios.post(`${API}/api/otp/email/verify`, { email: regEmail.trim().toLowerCase(), code: regOtpCode });
      setRegOtpVerified(true);
      setInfo("Email verified — now finish creating your account below.");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Incorrect code — try again.");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    clearMsgs();
    if (!regOtpVerified) { setError("Verify your email with the 6-digit code first."); return; }
    setSubmitting(true);
    try {
      await register({ email: regEmail.trim(), username: regUsername.trim(), password: regPassword, name: regName.trim() });
      nav("/?welcome=1", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const requestForgotCode = async (e) => {
    e.preventDefault();
    clearMsgs();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/students/forgot-password`, { email: forgotEmail.trim() });
      setInfo("If an account exists for that email, we've sent a 6-digit code. It expires in 10 minutes.");
      setForgotStage("verify");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    clearMsgs();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/students/reset-password`, {
        email: forgotEmail.trim(),
        code: forgotCode.trim(),
        new_password: forgotNewPassword,
      });
      setInfo("Password reset! Signing you in with your new password will now work.");
      setTab("signin");
      setIdentifier(forgotEmail.trim());
      setPassword("");
      setForgotStage("email");
      setForgotCode("");
      setForgotNewPassword("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f6f2] grain relative overflow-hidden student-profile">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 55% 40% at 80% 12%, rgba(219,9,9,0.10), transparent 65%), radial-gradient(ellipse 45% 40% at 15% 88%, rgba(219,9,9,0.06), transparent 65%)" }}
      />

      <div className="relative z-10 max-w-md mx-auto px-6 pt-16 pb-24">
        <Link
          to="/"
          data-testid="login-back-link"
          className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30] hover:text-[#DB0909] transition-colors"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8"
        >
          <div className="flex items-center gap-3">
            <Logo className="h-10" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#950606]">abc guitar lessons</span>
          </div>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl text-[#1c1610] leading-[1.02]">
            {tab === "signin" && <>Welcome <em className="italic text-[#DB0909]">back.</em></>}
            {tab === "register" && <>Join the <em className="italic text-[#DB0909]">band.</em></>}
            {tab === "forgot" && <>Reset <em className="italic text-[#DB0909]">password.</em></>}
          </h1>
          <p className="mt-3 text-sm text-[#4a3e30] font-light">
            {tab === "signin" && "Your class schedule, materials, and announcements live here."}
            {tab === "register" && "Minimal signup. We'll auto-link your existing bookings."}
            {tab === "forgot" && "We'll email you a 6-digit code."}
          </p>
        </motion.div>

        <div className="mt-8 flex gap-1 border-b border-[rgba(28,22,16,0.14)]" data-testid="login-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`login-tab-${t.id}`}
              onClick={() => switchTab(t.id)}
              className={`relative flex-1 font-mono text-[11px] tracking-[0.2em] uppercase py-3 transition-colors ${
                tab === t.id ? "text-[#1c1610]" : "text-[#4a3e30] hover:text-[#1c1610]"
              }`}
            >
              {t.label}
              {tab === t.id && (
                <motion.span layoutId="login-tab-underline" className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#DB0909]" />
              )}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {error && (
            <div data-testid="login-error" className="mb-5 border border-[#DB0909]/50 bg-[#DB0909]/[0.05] px-4 py-3 text-xs text-[#950606] font-light">
              {error}
            </div>
          )}
          {info && (
            <div data-testid="login-info" className="mb-5 border border-[#1c1610]/20 bg-[#f6efe3] px-4 py-3 text-xs text-[#1c1610] font-light">
              {info}
            </div>
          )}

          {tab === "signin" && (
            <form onSubmit={handleSignin} className="space-y-5" data-testid="signin-form">
              <InputRow
                label="Email or username"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@example.com"
                data-testid="signin-identifier"
                required
                autoComplete="username"
              />
              <PasswordRow
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                testId="signin-password"
              />
              <button
                type="submit"
                disabled={submitting || !identifier || !password}
                data-testid="signin-submit"
                className="w-full font-mono text-xs tracking-[0.3em] uppercase bg-[#1c1610] text-[#f7f6f2] py-4 hover:bg-[#DB0909] active:bg-[#950606] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? "Signing in…" : "Sign in →"}
              </button>
              <button
                type="button"
                onClick={() => switchTab("forgot")}
                className="w-full text-center font-mono text-[10px] tracking-[0.25em] uppercase text-[#950606] hover:text-[#DB0909] underline underline-offset-4 py-2"
              >
                Forgot password?
              </button>
            </form>
          )}

          {tab === "register" && (
            <form onSubmit={handleRegister} className="space-y-5" data-testid="register-form">
              <InputRow
                label="Full name"
                type="text"
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                placeholder="Your name"
                data-testid="register-name"
                required
              />
              <div>
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30] block mb-2">Email</span>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className={`flex-1 flex items-stretch border transition-colors ${
                    regOtpVerified ? "border-[#16a34a]/70 bg-[#16a34a]/[0.04]" : "border-[rgba(28,22,16,0.2)] focus-within:border-[#DB0909]"
                  }`}>
                    <input
                      type="email"
                      value={regEmail}
                      onChange={(e) => { setRegEmail(e.target.value); setRegOtpSent(false); setRegOtpVerified(false); setRegOtpCode(""); }}
                      placeholder="you@example.com"
                      data-testid="register-email"
                      required
                      readOnly={regOtpVerified}
                      autoComplete="email"
                      className="flex-1 bg-transparent px-4 py-3 text-sm text-[#1c1610] focus:outline-none placeholder:text-[#4a3e30]/50 disabled:cursor-not-allowed"
                    />
                    {regOtpVerified && (
                      <span data-testid="register-email-verified" className="inline-flex items-center gap-1 pr-3 font-mono text-[10px] tracking-[0.15em] uppercase text-[#16a34a]">
                        <CheckCircle2 size={12} /> Verified
                      </span>
                    )}
                  </div>
                  {!regOtpVerified && (
                    <button
                      type="button"
                      data-testid="register-send-otp-btn"
                      onClick={sendRegisterOtp}
                      disabled={regOtpSending || regOtpCooldown > 0 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail.trim())}
                      className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase bg-[#DB0909] text-[#f7f6f2] px-5 py-3 hover:bg-[#950606] active:bg-[#1c1610] disabled:bg-[#4a3e30]/40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      {regOtpSending ? "Sending…" : regOtpCooldown > 0 ? `Resend in ${regOtpCooldown}s` : regOtpSent ? "Resend Code" : "Verify Email"}
                    </button>
                  )}
                </div>
                {/* OTP input slides in */}
                <div
                  data-testid="register-otp-panel"
                  className={`overflow-hidden transition-all duration-500 ease-out ${
                    regOtpSent && !regOtpVerified ? "max-h-40 mt-3 opacity-100" : "max-h-0 opacity-0"
                  }`}
                  aria-hidden={!regOtpSent || regOtpVerified}
                >
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30]">6-digit code sent to <span className="text-[#1c1610]">{regEmail}</span></p>
                  <div className="mt-2 flex items-stretch border border-[rgba(28,22,16,0.2)] focus-within:border-[#DB0909]">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      value={regOtpCode}
                      onChange={(e) => setRegOtpCode(e.target.value.replace(/\D/g, ""))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); verifyRegisterOtp(); } }}
                      placeholder="000000"
                      data-testid="register-otp-code"
                      className="flex-1 bg-transparent px-4 py-3 text-sm text-[#1c1610] focus:outline-none placeholder:text-[#4a3e30]/40 font-mono tracking-[0.3em]"
                    />
                    <button
                      type="button"
                      data-testid="register-verify-otp-btn"
                      onClick={verifyRegisterOtp}
                      disabled={regOtpCode.length !== 6}
                      className="px-4 font-mono text-[10px] tracking-[0.15em] uppercase bg-[#1c1610] text-[#f7f6f2] hover:bg-[#DB0909] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Verify
                    </button>
                  </div>
                </div>
              </div>
              <InputRow
                label="Username"
                type="text"
                value={regUsername}
                onChange={(e) => setRegUsername(e.target.value.replace(/[^a-zA-Z0-9_.\-]/g, ""))}
                placeholder="stringbender"
                data-testid="register-username"
                required
                minLength={3}
                maxLength={32}
              />
              <PasswordRow
                label="Password (8+ chars)"
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                testId="register-password"
              />
              <button
                type="submit"
                disabled={submitting || !regOtpVerified || !regEmail || !regUsername || regPassword.length < 8 || regName.length < 2}
                data-testid="register-submit"
                className="w-full font-mono text-xs tracking-[0.3em] uppercase bg-[#1c1610] text-[#f7f6f2] py-4 hover:bg-[#DB0909] active:bg-[#950606] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? "Creating…" : regOtpVerified ? "Create account →" : "Verify email first"}
              </button>
              <p className="text-center font-mono text-[10px] tracking-[0.15em] uppercase text-[#4a3e30]">
                By registering, you agree to receive class updates & materials by email.
              </p>
            </form>
          )}

          {tab === "forgot" && (
            <>
              {forgotStage === "email" && (
                <form onSubmit={requestForgotCode} className="space-y-5" data-testid="forgot-form">
                  <InputRow
                    label="Account email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="forgot-email"
                    required
                  />
                  <button
                    type="submit"
                    disabled={submitting || !forgotEmail}
                    data-testid="forgot-submit"
                    className="w-full font-mono text-xs tracking-[0.3em] uppercase bg-[#1c1610] text-[#f7f6f2] py-4 hover:bg-[#DB0909] active:bg-[#950606] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? "Sending code…" : "Send reset code →"}
                  </button>
                </form>
              )}
              {forgotStage === "verify" && (
                <form onSubmit={resetPassword} className="space-y-5" data-testid="reset-form">
                  <InputRow
                    label="6-digit code"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={forgotCode}
                    onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    data-testid="reset-code"
                    required
                  />
                  <PasswordRow
                    label="New password (8+ chars)"
                    value={forgotNewPassword}
                    onChange={(e) => setForgotNewPassword(e.target.value)}
                    testId="reset-new-password"
                  />
                  <button
                    type="submit"
                    disabled={submitting || forgotCode.length !== 6 || forgotNewPassword.length < 8}
                    data-testid="reset-submit"
                    className="w-full font-mono text-xs tracking-[0.3em] uppercase bg-[#1c1610] text-[#f7f6f2] py-4 hover:bg-[#DB0909] active:bg-[#950606] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? "Resetting…" : "Reset password →"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setForgotStage("email"); clearMsgs(); }}
                    className="w-full text-center font-mono text-[10px] tracking-[0.25em] uppercase text-[#950606] hover:text-[#DB0909] underline underline-offset-4 py-2"
                  >
                    ← Resend or use another email
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        <p className="mt-12 font-mono text-[9px] tracking-[0.3em] uppercase text-[#4a3e30]/70 text-center">
          ABC Guitar Lessons · English · Hindi · Malayalam
        </p>
      </div>
    </div>
  );
}
